export enum IMAGE_STATUS {
	IDLE = 'IDLE',
	LOADING = 'LOADING',
	READY = 'READY',
	ERROR = 'ERROR',
}

export type ImageState =
	| { status: IMAGE_STATUS.IDLE }
	| { status: IMAGE_STATUS.LOADING }
	| { status: IMAGE_STATUS.READY; url: string }
	| { status: IMAGE_STATUS.ERROR; error?: unknown };

type Listener = () => void;

const IDLE_STATE: ImageState = Object.freeze({ status: IMAGE_STATUS.IDLE });

class ImageCache {
	private imageStatesByKey = new Map<string, ImageState>();
	private inflightRequestsByKey = new Map<string, Promise<string>>();
	private subscribersByKey = new Map<string, Set<Listener>>();

	getImageStateByKey(key: string): ImageState {
		return this.imageStatesByKey.get(key) ?? IDLE_STATE;
	}

	addSubscriber(key: string, listener: Listener): () => void {
		let subscriberSet = this.subscribersByKey.get(key);

		if (!subscriberSet) {
			subscriberSet = new Set();
			this.subscribersByKey.set(key, subscriberSet);
		}

		subscriberSet.add(listener);

		return () => {
			subscriberSet!.delete(listener);

			if (subscriberSet!.size === 0) {
				this.subscribersByKey.delete(key);
			}
		};
	}

	private notifySubscribersForKey(key: string) {
		const subscriberSet = this.subscribersByKey.get(key);

		if (!subscriberSet) {
			return;
		}

		for (const notifyFunction of subscriberSet) {
			notifyFunction();
		}
	}

	async fetch(key: string, loader: () => Promise<string>): Promise<string> {
		const currentImageState = this.imageStatesByKey.get(key);

		if (currentImageState?.status === IMAGE_STATUS.READY) {
			return currentImageState.url;
		}

		const currentRequest = this.inflightRequestsByKey.get(key);
		if (currentRequest) {
			return currentRequest;
		}

		this.imageStatesByKey.set(key, { status: IMAGE_STATUS.LOADING });
		this.notifySubscribersForKey(key);

		const promise = (async () => {
			try {
				const url = await loader();

				this.imageStatesByKey.set(key, { status: IMAGE_STATUS.READY, url });
				this.notifySubscribersForKey(key);

				return url;
			} catch (err) {
				const normalized = normalizeError(err);

				this.imageStatesByKey.set(key, { status: IMAGE_STATUS.ERROR, error: normalized });
				this.notifySubscribersForKey(key);

				return '';
			} finally {
				this.inflightRequestsByKey.delete(key);
			}
		})();

		this.inflightRequestsByKey.set(key, promise);
		return promise;
	}
}

function normalizeError(error: unknown): Error {
	if (error instanceof Error) {
		return error;
	}

	try {
		return new Error(typeof error === 'string' ? error : JSON.stringify(error));
	} catch {
		return new Error(String(error));
	}
}

export const imageCache = new ImageCache();
